import { useMutation } from '@tanstack/react-query';
import type { Session, User } from '@supabase/supabase-js';
import type { SupportedLocale } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';

export interface AdminEmailOtpRequestInput {
  email: string;
  preferredLocale?: SupportedLocale;
  captchaToken?: string;
}

export interface AdminEmailOtpVerifyInput {
  email: string;
  token: string;
  captchaToken?: string;
}

export interface AdminEmailOtpRequestResult {
  messageId: string | null;
}

export interface AdminEmailOtpVerifyResult {
  user: User | null;
  session: Session | null;
}

export function useRequestAdminEmailOtp() {
  return useMutation<AdminEmailOtpRequestResult, unknown, AdminEmailOtpRequestInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.auth.signInWithOtp({
        email: input.email,
        options: {
          shouldCreateUser: true,
          data: { merchant_signup: 'true', preferred_locale: input.preferredLocale ?? 'ar' },
          ...(input.captchaToken ? { captchaToken: input.captchaToken } : {}),
        },
      });
      if (error) throw error;
      return { messageId: data.messageId ?? null };
    },
  });
}

export const useSignin = useRequestAdminEmailOtp;

export function useVerifyAdminEmailOtp() {
  return useMutation<AdminEmailOtpVerifyResult, unknown, AdminEmailOtpVerifyInput>({
    mutationFn: async (input) => {
      const { data, error } = await supabase.auth.verifyOtp({
        email: input.email,
        token: input.token,
        type: 'email',
        ...(input.captchaToken
          ? {
              options: {
                captchaToken: input.captchaToken,
              },
            }
          : {}),
      });
      if (error) throw error;
      return { user: data.user, session: data.session };
    },
  });
}
